import { useEffect, useRef, useCallback } from 'react';
import { Track, RoomEvent } from 'livekit-client';
import { useLiveKitSession } from './LiveKitSessionContext';
import { getTimelineBrowserFields } from './clientBrowserInfo';
import { createTimelineOutbox } from './recordingTimelineOutbox';

/**
 * Maps LiveKit local track state to server timeline (mic/cam/screen: off | muted | on).
 * Used for client_media_snapshot and aligns with server timeline / audits.
 */
export function deriveMediaSnapshot(local) {
  if (!local) return null;
  const micPub = local.getTrack(Track.Source.Microphone);
  const camPub = local.getTrack(Track.Source.Camera);
  const screenPub = local.getTrack(Track.Source.ScreenShare);

  let microphone = 'off';
  if (micPub?.track) {
    microphone = micPub.isMuted ? 'muted' : 'on';
  } else if (local.isMicrophoneEnabled) {
    microphone = 'on';
  } else {
    microphone = 'off';
  }

  let camera = 'off';
  if (camPub?.track) {
    camera = camPub.isMuted ? 'muted' : 'on';
  } else if (local.isCameraEnabled) {
    camera = 'on';
  } else {
    camera = 'off';
  }

  const screenshareOn = Boolean(screenPub?.track);

  return { microphone, camera, screenshareOn };
}

/** Baseline remote state when recording starts (webhooks do not replay pre-existing tracks). */
export function deriveRemoteMediaSnapshot(participant) {
  if (!participant) return null;
  const micPub = participant.getTrackPublication(Track.Source.Microphone);
  const camPub = participant.getTrackPublication(Track.Source.Camera);
  const screenPub = participant.getTrackPublication(Track.Source.ScreenShare);

  let microphone = 'off';
  if (micPub?.track) {
    microphone = micPub.isMuted ? 'muted' : 'on';
  } else if (participant.isMicrophoneEnabled) {
    microphone = 'on';
  } else {
    microphone = 'off';
  }

  let camera = 'off';
  if (camPub?.track) {
    camera = camPub.isMuted ? 'muted' : 'on';
  } else if (participant.isCameraEnabled) {
    camera = 'on';
  } else {
    camera = 'off';
  }

  const screenshareOn = Boolean(screenPub?.track);
  return { microphone, camera, screenshareOn };
}

/**
 * While `recording` is true, POSTs mic/cam/screenshare transitions to the token server
 * Redis timeline via an at-least-once outbox (TI-407): events emitted before the telemetry
 * JWT arrives are BUFFERED (not dropped) and flushed once it lands, transient network
 * failures are retried with backoff, and the queue is flushed on page unload. Telemetry
 * JWT comes from LiveKitSessionProvider + TelemetryBootstrap.
 */
export function useRecordingTimelineReporter(room, recording) {
  const { telemetryJwt } = useLiveKitSession();
  const roomName = room?.name;
  const local = room?.localParticipant;
  const identity = local?.identity;
  const participantSid = local?.sid;

  // Latest JWT for the outbox to read at send time (survives buffering across its arrival).
  const telemetryJwtRef = useRef(telemetryJwt);
  telemetryJwtRef.current = telemetryJwt;

  // One stable outbox per reporter instance; reads the current JWT lazily.
  const outboxRef = useRef(null);
  if (!outboxRef.current) {
    outboxRef.current = createTimelineOutbox({ getJwt: () => telemetryJwtRef.current });
  }

  /** Enqueue a timeline event (browser fields merged in); delivery + retry owned by the outbox. */
  const enqueue = useCallback((fields) => {
    outboxRef.current.enqueue({ ...getTimelineBrowserFields(), ...fields });
  }, []);

  // Drain buffered pre-JWT events the moment the telemetry JWT becomes available.
  useEffect(() => {
    if (telemetryJwt) outboxRef.current.flush();
  }, [telemetryJwt]);

  // Best-effort flush of anything still queued when the tab is closed/backgrounded.
  useEffect(() => {
    if (!recording) return undefined;
    const onHide = () => outboxRef.current.flushBeacon();
    const onVisibility = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
        outboxRef.current.flushBeacon();
      }
    };
    window.addEventListener('pagehide', onHide);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('pagehide', onHide);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [recording]);

  const warnedNoTelemetryRef = useRef(false);
  useEffect(() => {
    if (!recording) {
      warnedNoTelemetryRef.current = false;
      return;
    }
    if (telemetryJwt) {
      warnedNoTelemetryRef.current = false;
      return;
    }
    if (!warnedNoTelemetryRef.current) {
      warnedNoTelemetryRef.current = true;
      console.warn(
        "[LiveKit] Recording is active but there is no telemetry JWT yet — timeline events are BUFFERED and will flush once it arrives. " +
          "Open Network: expect POST .../livekit/telemetry/token after connect. If missing, fix CORS or REACT_APP_TOKEN_API_URL."
      );
    }
  }, [recording, telemetryJwt]);

  const post = useCallback(
    (event, extra = {}) => {
      if (!recording || !roomName || !identity) return;
      enqueue({
        roomName,
        identity,
        participantSid,
        event,
        ...extra,
      });
    },
    [recording, roomName, identity, participantSid, enqueue]
  );

  const snapshotRef = useRef({ active: false, sid: null });

  useEffect(() => {
    if (!recording) {
      snapshotRef.current = { active: false, sid: null };
      return;
    }
    if (!local || !roomName || !room) return;

    const sid = local.sid;
    const prev = snapshotRef.current;
    if (prev.active && prev.sid === sid) return;

    snapshotRef.current = { active: true, sid };
    const snap = deriveMediaSnapshot(local);
    if (snap) post('client_media_snapshot', snap);

    for (const participant of room.remoteParticipants?.values() ?? []) {
      const rsnap = deriveRemoteMediaSnapshot(participant);
      if (!rsnap || !participant.identity) continue;
      enqueue({
        roomName,
        identity: participant.identity,
        participantSid: participant.sid,
        event: 'client_media_snapshot',
        microphone: rsnap.microphone,
        camera: rsnap.camera,
        screenshareOn: rsnap.screenshareOn,
      });
    }
  }, [recording, roomName, local, post, room, enqueue]);

  useEffect(() => {
    if (!recording || !local) return;

    const onMuted = (pub) => {
      if (!pub) return;
      if (pub.source === Track.Source.Microphone) post('client_microphone_muted');
      if (pub.source === Track.Source.Camera) post('client_camera_muted');
    };

    const onUnmuted = (pub) => {
      if (!pub) return;
      if (pub.source === Track.Source.Microphone) post('client_microphone_unmuted');
      if (pub.source === Track.Source.Camera) post('client_camera_unmuted');
    };

    const onPublished = (pub) => {
      if (!pub) return;
      if (pub.source === Track.Source.Microphone) {
        post(pub.isMuted ? 'client_microphone_muted' : 'client_microphone_on');
      }
      if (pub.source === Track.Source.Camera) {
        post(pub.isMuted ? 'client_camera_muted' : 'client_camera_on');
      }
      if (pub.source === Track.Source.ScreenShare) {
        post('client_screenshare_started');
      }
    };

    const onUnpublished = (pub) => {
      if (!pub) return;
      if (pub.source === Track.Source.Microphone) post('client_microphone_off');
      if (pub.source === Track.Source.Camera) post('client_camera_off');
      if (pub.source === Track.Source.ScreenShare) post('client_screenshare_stopped');
    };

    local.on('trackMuted', onMuted);
    local.on('trackUnmuted', onUnmuted);
    local.on('localTrackPublished', onPublished);
    local.on('localTrackUnpublished', onUnpublished);

    return () => {
      local.off('trackMuted', onMuted);
      local.off('trackUnmuted', onUnmuted);
      local.off('localTrackPublished', onPublished);
      local.off('localTrackUnpublished', onUnpublished);
    };
  }, [recording, local, post]);

  /** Remote participants: each browser reports what it observes; server dedupes multi-client duplicates. */
  useEffect(() => {
    if (!recording || !room?.name) return;

    const postForPeer = (event, participant) => {
      if (!participant?.identity) return;
      enqueue({
        roomName: room.name,
        identity: participant.identity,
        participantSid: participant.sid,
        event,
      });
    };

    const onMuted = (pub, participant) => {
      if (!pub || participant?.isLocal) return;
      if (pub.source === Track.Source.Microphone) postForPeer('client_microphone_muted', participant);
      if (pub.source === Track.Source.Camera) postForPeer('client_camera_muted', participant);
    };

    const onUnmuted = (pub, participant) => {
      if (!pub || participant?.isLocal) return;
      if (pub.source === Track.Source.Microphone) postForPeer('client_microphone_unmuted', participant);
      if (pub.source === Track.Source.Camera) postForPeer('client_camera_unmuted', participant);
    };

    const onPublished = (pub, participant) => {
      if (!pub) return;
      if (pub.source === Track.Source.ScreenShare) {
        postForPeer('client_screenshare_started', participant);
        return;
      }
      if (pub.source === Track.Source.Microphone) {
        postForPeer(pub.isMuted ? 'client_microphone_muted' : 'client_microphone_on', participant);
      }
      if (pub.source === Track.Source.Camera) {
        postForPeer(pub.isMuted ? 'client_camera_muted' : 'client_camera_on', participant);
      }
    };

    const onUnpublished = (pub, participant) => {
      if (!pub) return;
      if (pub.source === Track.Source.Microphone) postForPeer('client_microphone_off', participant);
      if (pub.source === Track.Source.Camera) postForPeer('client_camera_off', participant);
      if (pub.source === Track.Source.ScreenShare) postForPeer('client_screenshare_stopped', participant);
    };

    room.on(RoomEvent.TrackMuted, onMuted);
    room.on(RoomEvent.TrackUnmuted, onUnmuted);
    room.on(RoomEvent.TrackPublished, onPublished);
    room.on(RoomEvent.TrackUnpublished, onUnpublished);

    return () => {
      room.off(RoomEvent.TrackMuted, onMuted);
      room.off(RoomEvent.TrackUnmuted, onUnmuted);
      room.off(RoomEvent.TrackPublished, onPublished);
      room.off(RoomEvent.TrackUnpublished, onUnpublished);
    };
  }, [recording, room, enqueue]);
}
